'use client';

import { Suspense, useCallback, useEffect, useState } from 'react';

import { useRouter } from 'next/navigation';

import { ICompany } from 'types/company';
import { IStory } from 'types/story';

import { useBreakPoint, useMediaQuery } from 'hooks/browser/mediaQueries';
import { useResizeEffect } from 'hooks/browser/resizeEffect';
import { useSetupStoryState } from 'hooks/stories/state';
import { useFullscreenMode } from 'hooks/stories/viewer/useFullscreenMode';
import { useStoryChromeScale } from 'hooks/stories/viewer/useStoryChromeScale';
import { usePageTracking } from 'hooks/tracking';

import { CloseIcon } from 'components/icons/CloseIcon';
import { FullscreenIcon } from 'components/icons/FullscreenIcon';
import Page from 'components/shared/layout/Page';
import { PageFooter } from 'components/shared/layout/PageFooter';
import { StorySlider } from 'components/story/slider/StorySlider';

interface IProps {
  company: ICompany;
  story: IStory;
}

const SliderViewContent = ({ story, company }: IProps) => {
  usePageTracking('Story: Slider');

  const { push } = useRouter();
  const { matches: isPortraitLg } = useMediaQuery(
    '(min-width: 768px) and (orientation: portrait)',
  );
  const { isFullscreen, toggleFullscreen } = useFullscreenMode(!!isPortraitLg);
  const { isMobile } = useBreakPoint();
  // Fullscreen UI is desktop-only — mobile keeps the normal layout.
  const applyFullscreen = isFullscreen && !isMobile;
  const { lazySetupStoryState } = useSetupStoryState();

  const [innerHeight, setInnerHeight] = useState<number>();
  const updateInnerHeight = useCallback(() => {
    setInnerHeight(window.innerHeight);
  }, []);
  useResizeEffect(updateInnerHeight);

  const { closeIconSize } = useStoryChromeScale();

  useEffect(() => {
    lazySetupStoryState(story.id, 'view', story.meta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    if (window.opener) {
      window.close();
    } else {
      push(`/${company.slug}`);
    }
  };

  const controls = (iconSize: number, backgroundColor?: string) => (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={toggleFullscreen}
        className="opacity-40 transition-opacity hover:opacity-100"
        aria-label={applyFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        aria-pressed={applyFullscreen}
      >
        <FullscreenIcon
          size={iconSize}
          backgroundColor={backgroundColor}
          active={applyFullscreen}
        />
      </button>

      <button
        type="button"
        onClick={handleClose}
        className="opacity-40 transition-opacity hover:opacity-100"
        aria-label="Close story"
      >
        <CloseIcon size={iconSize} backgroundColor={backgroundColor} />
      </button>
    </div>
  );

  return (
    <Page>
      <main
        className={
          applyFullscreen
            ? 'relative flex h-screen flex-col overflow-hidden'
            : 'relative flex flex-col justify-start'
        }
        style={
          applyFullscreen
            ? undefined
            : {
              minHeight: `${innerHeight}px`,
            }
        }
      >
        {!applyFullscreen && (
          <div className="relative hidden w-full px-5 pt-[60px] text-center text-[38px] font-bold text-black dark:text-white md:block md:px-16">
            {story.title}

            <div
              className="absolute right-16"
              style={{ top: 'calc(50% + 5px)' }}
            >
              {controls(closeIconSize, 'none')}
            </div>
          </div>
        )}

        <StorySlider
          story={story}
          company={company}
          isFullscreen={applyFullscreen}
          fullscreenControls={applyFullscreen ? controls(40) : undefined}
        />

        {!applyFullscreen && (
          <div className="hidden md:block">
            <PageFooter inStory />
          </div>
        )}
      </main>
    </Page>
  );
};

export const SliderView = (props: IProps) => (
  <Suspense>
    <SliderViewContent {...props} />
  </Suspense>
);
